import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

/** 管理メニュー個別権限の1行（`admin_menu_user_permissions`）。 */
export interface AdminMenuPermissionRow {
  menu_key: string;
  is_visible: boolean;
  sort_order: number | null;
}

/** 指定ユーザーの管理メニュー個別権限を取得する。 */
export async function fetchAdminMenuPermissions(
  userId: string,
): Promise<AdminMenuPermissionRow[]> {
  const { data, error } = await supabase
    .from('admin_menu_user_permissions')
    .select('menu_key, is_visible, sort_order')
    .eq('user_id', userId);
  if (error) throw error;
  return (data as AdminMenuPermissionRow[]) ?? [];
}

/** 指定ユーザーの管理メニュー個別権限を保存（上書き）する。 */
export async function saveAdminMenuPermissions(
  userId: string,
  entries: { menuKey: string; isVisible: boolean; sortOrder: number | null }[],
): Promise<void> {
  for (const entry of entries) {
    const { error } = await supabase.from('admin_menu_user_permissions').upsert(
      {
        user_id: userId,
        menu_key: entry.menuKey,
        is_visible: entry.isVisible,
        sort_order: entry.sortOrder,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,menu_key' },
    );
    if (error) throw error;
  }
}

/**
 * ログインユーザー自身に許可された管理メニューの個別許可を取得する共通フック。
 *
 * 利用者（welfare_user）のダッシュボードに「個別許可された管理メニュー」を追加表示する用途と、
 * `AdminAccessGuard` が「許可されたページだけ開かせる」用途で使う。
 * 未設定（行なし）の場合は空配列を返す（＝何も許可されていない）。
 */
export function useMyAdminMenuPermissions(enabled: boolean) {
  const { user } = useAuth();
  const [rows, setRows] = useState<AdminMenuPermissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!enabled || !user?.id) {
      setRows([]);
      setLoading(false);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await fetchAdminMenuPermissions(user.id);
      setRows(data);
    } catch (err) {
      console.error('管理メニュー権限の取得エラー:', err);
      setError('管理メニュー権限の取得に失敗しました。');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  return { rows, loading, error, reload: load };
}

export default useMyAdminMenuPermissions;